// Ícones de veículo do EcooGPS — conjunto 2D.
//
// O EcooGPS escolhe o ícone pela `category` do device (que a API
// devolve e o nosso provider guarda como `vehicle.type`). Aqui:
//   • mapeamos a categoria → uma das 21 categorias 2D conhecidas;
//   • carregamos o PNG `{categoria}_{cor}.png` e convertemo-lo em
//     data-URI base64, para poder ser embebido (e rodado) no SVG do
//     marcador composto.
//
// Os PNGs estão em public/vehicle-icons/2d/ (copiados do EcooGPS).

// Zoom relativo por categoria — valores EXACTOS do deviceCategories.js do
// EcooGPS (2D + 3D rotativo + 3D estático). Passámos a servir também os
// frames 3d/3d_static (mount /vehicle-icons/{3d,3d_static}/), por isso já
// NÃO mapeamos 3D→2D: renderizamos o modelo real, igual ao EcooGPS.
const CAT_ZOOM = {
  // 2D
  car: 1.2, jeep2: 1.3, pickup: 1.2, jeep1: 1.3, van: 1.2, truck3: 1.2, bike: 1.1,
  truck: 1.7, tanker: 1.8, truck2: 1.4, crane: 1.7, circle: 0.75, circle2: 1.0,
  circle3: 1.25, machine: 1.5, machine4: 1.7, machine3: 1.4, machine2: 1.1,
  lifter: 1.4, boat: 1.8, sedan: 1.2,
  // 3D rotativos (3d_vehicle_N)
  '3d_vehicle_1': 1.5, '3d_vehicle_3': 1.5, '3d_vehicle_29': 1.3, '3d_vehicle_16': 1.6,
  '3d_vehicle_7': 1.7, '3d_vehicle_5': 1.5, '3d_vehicle_21': 1.9, '3d_vehicle_2': 1.6,
  '3d_vehicle_28': 1.9, '3d_vehicle_20': 1.6, '3d_vehicle_10': 1.5, '3d_vehicle_13': 1.3,
  '3d_vehicle_17': 1.4, '3d_vehicle_31': 1.9, '3d_vehicle_30': 1.7, '3d_vehicle_32': 2.4,
  '3d_vehicle_9': 2.4, '3d_vehicle_8': 1.9, '3d_vehicle_25': 2.0, '3d_vehicle_23': 2.2,
  '3d_vehicle_11': 1.9, '3d_vehicle_24': 2.5, '3d_vehicle_12': 2.4, '3d_vehicle_6': 2.0,
  '3d_vehicle_15': 2.3, '3d_vehicle_19': 2.1, '3d_vehicle_26': 2.0, '3d_vehicle_22': 1.6,
  '3d_vehicle_34': 1.7, '3d_vehicle_18': 2.1, '3d_vehicle_4': 1.8, '3d_vehicle_14': 1.8,
  '3d_vehicle_27': 2.0, '3d_vehicle_33': 2.1,
  // 3D estáticos (3d_static_imageN)
  '3d_static_image1': 1.5, '3d_static_image2': 1.6, '3d_static_image3': 1.6, '3d_static_image4': 2.1,
  '3d_static_image5': 1.6, '3d_static_image6': 2.0, '3d_static_image7': 2.8, '3d_static_image8': 2.4,
  '3d_static_image9': 1.8, '3d_static_image10': 2.0, '3d_static_image12': 2.0, '3d_static_image13': 1.5,
  '3d_static_image14': 1.5, '3d_static_image15': 1.6, '3d_static_image16': 1.7, '3d_static_image17': 1.4,
  '3d_static_image18': 1.7, '3d_static_image19': 1.6, '3d_static_image20': 1.9, '3d_static_image21': 1.6,
  '3d_static_image22': 1.8, '3d_static_image23': 2.3, '3d_static_image24': 1.3, '3d_static_image25': 1.7,
  '3d_static_image26': 1.8, '3d_static_image27': 1.7, '3d_static_image28': 2.0, '3d_static_image29': 2.0,
  '3d_static_image30': 1.8, '3d_static_image31': 2.3,
};

// Sinónimos / categorias Traccar standard → categoria 2D conhecida (só para
// tipos que NÃO são já categorias EcooGPS 2D/3D).
const SYNONYM_TO_2D = {
  motorcycle: 'bike', motorbike: 'bike', scooter: 'bike', moto: 'bike',
  bus: 'truck2', minibus: 'van', autocarro: 'truck2', carrinha: 'van',
  tractor: 'machine3', trator: 'machine3', offroad: 'jeep1', suv: 'jeep1',
  jeep: 'jeep2', ship: 'boat', barco: 'boat', trailer: 'truck',
  camiao: 'truck', 'camião': 'truck', cisterna: 'tanker', default: 'car',
};

// Categoria REAL do device (mantém 3d_vehicle_/3d_static_ para render nativo).
//   1) categoria conhecida (2D/3D/3D-estático) → tal e qual;
//   2) sinónimo Traccar → 2D correspondente;
//   3) desconhecido → 'car'.
export function vehicleIconCategory(type) {
  const t = String(type || '').toLowerCase().trim();
  if (CAT_ZOOM[t] != null) return t;
  if (SYNONYM_TO_2D[t]) return SYNONYM_TO_2D[t];
  return 'car';
}

export function vehicleIconZoom(cat) {
  return CAT_ZOOM[cat] || 1.2;
}

// Conjunto do ícone: '2d' | '3d' (rotativo) | '3d_static'.
function iconKind(cat) {
  if (String(cat).startsWith('3d_static')) return '3d_static';
  if (String(cat).startsWith('3d_vehicle')) return '3d';
  return '2d';
}

// Índice de ângulo (0000..0330, passo 30°) para o frame 3D pelo rumo.
function iconAngle(heading) {
  let a = Math.round((((Number(heading) || 0) % 360) + 360) % 360 / 30) * 30;
  if (a >= 360) a = 0;
  return String(a).padStart(4, '0');
}

// Rotação a aplicar ao ícone no marcador:
//   2d → rumo completo; 3d_static → 0 (estático); 3d → só a correcção fina
//   entre o rumo e o passo de 30° do frame pré-rodado (igual ao EcooGPS).
export function vehicleIconRotation(cat, heading) {
  const h = (((Number(heading) || 0) % 360) + 360) % 360;
  const kind = iconKind(cat);
  if (kind === '3d_static') return 0;
  if (kind === '3d') {
    let iconAng = Math.round(h / 30) * 30; if (iconAng >= 360) iconAng = 0;
    let corr = h - iconAng; if (corr < 0) corr += 360; else if (corr > 359) corr -= 360;
    return corr;
  }
  return h;
}

// ── Cache de data-URIs ───────────────────────────────────────────
// cache[key] : string (data-URI) | null (falhou) | undefined (por carregar)
const cache = {};
const pending = {};
let subscribers = [];

/**
 * Devolve o data-URI base64 do ícone {cat}_{color}, ou:
 *   • undefined — ainda a carregar (carrega em background);
 *   • null      — o ficheiro não existe / falhou.
 * Quando termina de carregar, notifica os subscritores (re-render).
 */
export function getVehicleIconDataUrl(cat, color, heading) {
  const kind = iconKind(cat);
  let key, path;
  if (kind === '3d') {
    const ang = iconAngle(heading);
    key = `3d/${cat}_${color}_${ang}`;
    path = `/vehicle-icons/3d/${cat}_${color}_${ang}.png`;
  } else if (kind === '3d_static') {
    key = `3ds/${cat}_${color}`;
    path = `/vehicle-icons/3d_static/${cat}_${color}.png`;
  } else {
    key = `2d/${cat}_${color}`;
    path = `/vehicle-icons/2d/${cat}_${color}.png`;
  }
  if (key in cache) return cache[key];
  if (!pending[key]) {
    pending[key] = true;
    fetch(path)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.blob(); })
      .then(blob => new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      }))
      .then(dataUrl => {
        cache[key] = dataUrl;
        subscribers.forEach(fn => { try { fn(); } catch { /* ignore */ } });
      })
      .catch(() => { cache[key] = null; });
  }
  return undefined;
}

// Subscreve o evento "ícone carregado" — devolve a função de cancelar.
export function onVehicleIconLoaded(fn) {
  subscribers.push(fn);
  return () => { subscribers = subscribers.filter(x => x !== fn); };
}

// ── Construtor do marcador de veículo ────────────────────────────
// Ícone EcooGPS do tipo (rodado pelo rumo) + perna a 45° + bolha com
// a etiqueta. Enquanto o PNG do tipo não carrega (ou se falhar) usa a
// bolinha de estado — fallback que nunca deixa o marcador em branco.
// Devolve { url, w, h, ax, ay } — (ax,ay) = âncora na posição GPS.
const STATUS_COLOR = { green: '#22c55e', yellow: '#eab308', red: '#ef4444', gray: '#94a3b8' };

function escapeXml(s) {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

export function buildVehicleMarkerIcon({ label, type, status = 'gray', heading = 0 } = {}) {
  const bg = STATUS_COLOR[status] || STATUS_COLOR.gray;
  const raw = String(label || '—');
  const text = escapeXml(raw.toUpperCase());
  const charW = 6, padX = 8, pad = 4, legV = 16;
  const bubbleW = Math.max(54, Math.min(170, padX * 2 + raw.length * charW));
  const bubbleH = 17;
  const shadow = '<defs><filter id="vmi" x="-40%" y="-20%" width="180%" height="150%">'
    + '<feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#000" flood-opacity="0.28"/></filter></defs>';
  const bubble = (bx, by) =>
    `<g filter="url(#vmi)"><rect x="${bx}" y="${by}" width="${bubbleW}" height="${bubbleH}" rx="5" ry="5" fill="${bg}"/></g>`
    + `<text x="${bx + bubbleW / 2}" y="${by + bubbleH / 2 + 0.5}" text-anchor="middle" dominant-baseline="central"`
    + ` font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="10" font-weight="700"`
    + ` fill="#ffffff" letter-spacing="0.2">${text}</text>`;

  const cat = vehicleIconCategory(type);
  const b64 = getVehicleIconDataUrl(cat, status === 'gray' ? 'gray' : status);

  let svg, w, h, ax, ay;
  if (b64) {
    const S = Math.round(Math.max(24, Math.min(54, 30 * vehicleIconZoom(cat))));
    const half = S / 2;
    const diag = Math.ceil(half * 1.42);
    const cx = diag + pad;
    const cy = Math.max(diag, legV + bubbleH) + pad;
    const bx = cx + legV;
    const by = cy - legV - bubbleH;
    w = Math.max(cx + diag, bx + bubbleW) + pad;
    h = cy + diag + pad;
    ax = cx; ay = cy;
    const hd = ((Number(heading) || 0) % 360 + 360) % 360;
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${shadow}`
      + `<line x1="${cx}" y1="${cy}" x2="${bx}" y2="${cy - legV}" stroke="#0f172a" stroke-width="1.4" stroke-linecap="round"/>`
      + bubble(bx, by)
      + `<image href="${b64}" x="${cx - half}" y="${cy - half}" width="${S}" height="${S}" transform="rotate(${hd} ${cx} ${cy})"/>`
      + '</svg>';
  } else {
    const dotR = 7, dotB = 2.5, dotO = dotR + dotB;
    const dotCx = pad + dotO;
    const legTopX = dotCx + legV;
    const byy = pad;
    const dotCy = byy + bubbleH + legV;
    w = legTopX + bubbleW + pad;
    h = dotCy + dotO + pad;
    ax = dotCx; ay = dotCy;
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${shadow}`
      + `<line x1="${dotCx}" y1="${dotCy}" x2="${legTopX}" y2="${byy + bubbleH}" stroke="#0f172a" stroke-width="1.4" stroke-linecap="round"/>`
      + bubble(legTopX, byy)
      + `<circle cx="${dotCx}" cy="${dotCy}" r="${dotO}" fill="#fff" filter="url(#vmi)"/>`
      + `<circle cx="${dotCx}" cy="${dotCy}" r="${dotR}" fill="${bg}"/>`
      + '</svg>';
  }
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, w, h, ax, ay };
}
